import React, { useState, useRef } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { AuthGuard } from "../App";
import { useNavigate } from "react-router-dom";
// Import FontAwesomeIcon and specific icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faCreditCard, faCheckCircle } from "@fortawesome/free-solid-svg-icons";

const AgencyViewingFee = () => {
  const [webUrl, setWebUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showIframe, setShowIframe] = useState(false);
  const [showPaidMessage, setShowPaidMessage] = useState(false); // State to control "Already Paid" message
  
  // Directly retrieve user data here, inside the component, but before return
  // This ensures it's available for rendering and function calls
  const navigate = useNavigate();
  const iframeRef = useRef(null);

  // Consider fetching userId and token inside useEffect if they can change
  // For static local storage items, this is generally fine, but be aware of SSR or initial render issues
  // if localStorage isn't immediately available. For client-side React, it's usually okay.
  const token = localStorage.getItem("token");
  const userDataString = localStorage.getItem("user");
  const userData = userDataString ? JSON.parse(userDataString) : null;
  const userId = userData ? userData.unique : null;

  const handleCheckAgencyFee = async () => {
    setIsLoading(true);
    setShowPaidMessage(false); // Reset paid message when attempting a new check

    if (!userId || !token) {
      toast.error("User not authenticated. Please log in.");
      setIsLoading(false);
      navigate("/login"); // Redirect to login if user data is missing
      return;
    }

    try {
      const response = await axios.post(
        `https://backend.inyumba.co.ke/iNyumba/user/agencyviewingfee/check-payment-status/${userId}`,
        null,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (
        response.data.success === true &&
        response.data.response === "You have already paid. No need to pay again."
      ) {
        toast.info("You have already paid the agency viewing fee. ✅");
        setShowPaidMessage(true); // Show the dedicated "Paid" message
      } else {
        setWebUrl(response.data.response);
        toast.success("Please complete the payment in the pop-up window. 🚀");
        setShowIframe(true);
        // Optionally, reset showPaidMessage if a new payment is initiated after seeing "paid"
        setShowPaidMessage(false);
      }
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.response
        : "An unexpected error occurred while checking the agency fee.";
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseIframe = () => {
    setShowIframe(false); // Hide the iframe
    setWebUrl(""); // Clear the payment URL
    navigate("/user/myapartment"); // Navigate after closing
  };

  return (
    <AuthGuard>
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4 sm:p-6 lg:p-8">
        
        <div className="max-w-md w-full bg-white rounded-xl shadow-2xl overflow-hidden animate-fade-in">
          <div className="bg-gradient-to-r from-blue-700 to-purple-600 p-6 text-white text-center">
            <h1 className="text-3xl font-bold mb-2">Agency Viewing Fee 🏡</h1>
            <p className="text-lg opacity-90">Confirm payment for property viewings.</p>
          </div>

          <div className="p-8">
            <p className="text-gray-700 mb-6 text-center text-md leading-relaxed">
              To proceed with property viewings, a one-time agency viewing fee is required. Click the button below to check your payment status or initiate a new payment.
            </p>

            {showPaidMessage ? (
              <div className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 rounded-lg shadow-md flex items-center justify-center space-x-3 mb-6 animate-fade-in">
                <FontAwesomeIcon icon={faCheckCircle} className="text-3xl" />
                <p className="font-semibold text-lg">Payment Confirmed! You've already paid. 🎉</p>
              </div>
            ) : (
              <button
                onClick={handleCheckAgencyFee}
                className={`w-full py-3 px-6 rounded-lg text-lg font-semibold transition duration-300 ease-in-out flex items-center justify-center space-x-3 ${
                  isLoading
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-blue-600 text-white hover:bg-blue-700 transform hover:scale-105"
                }`}
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Checking...
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faCreditCard} className="text-xl" />
                    <span>Check Payment Status / Pay Fee</span>
                  </>
                )}
              </button>
            )}
            
            <p className="text-sm text-gray-500 mt-6 text-center italic">
              After payment, you will be redirected to your apartment details.
            </p>
          </div>
        </div>

        {/* Full-screen Iframe Modal for Payment */}
        {showIframe && webUrl && (
          <div className="fixed inset-0 bg-gray-900 bg-opacity-80 flex flex-col items-center justify-center z-50 p-4">
            <button
              onClick={handleCloseIframe}
              className="absolute top-4 right-4 bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-full text-lg shadow-lg transition-transform transform hover:scale-105 flex items-center space-x-2"
            >
              <FontAwesomeIcon icon={faTimes} />
              <span>Close Payment</span>
            </button>
            <iframe
              ref={iframeRef}
              src={webUrl}
              className="w-full h-full max-w-7xl max-h-[90vh] rounded-lg shadow-2xl border-none"
              title="Agency Fee Payment Gateway"
              allowFullScreen
            ></iframe>
          </div>
        )}
      </div>
    </AuthGuard>
  );
};

export default AgencyViewingFee;